import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Scale, FileText, Shield, CheckCircle2, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';

const PracticeAreas = () => {
  const { practiceAreas } = mockData;
  
  const iconMap = {
    FileText: FileText,
    Scale: Scale,
    Shield: Shield
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Practice Areas</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Comprehensive legal services across Tax Law, Bankruptcy Law, and Personal Injury. 
              We bring specialized expertise to every case.
            </p>
          </div>
        </div>
      </section>

      {/* Practice Areas Detailed */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto space-y-12">
            {practiceAreas.map((area, index) => {
              const IconComponent = iconMap[area.icon];
              return (
                <Card key={area.id} className="border-2 border-slate-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                      <div className="lg:col-span-1 bg-navy-900 p-8 flex flex-col items-center justify-center text-white">
                        <div className="w-24 h-24 bg-amber-600 rounded-full flex items-center justify-center mb-6">
                          <IconComponent className="h-12 w-12 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-center mb-4">{area.title}</h3>
                        <Link to="/contact">
                          <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                            Consult Now
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                      
                      <div className="lg:col-span-2 p-8 lg:p-12">
                        <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                          {area.description}
                        </p>
                        
                        <h4 className="text-xl font-bold text-navy-900 mb-6">Services Include:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                          {area.details.map((detail, idx) => (
                            <div key={idx} className="flex items-start">
                              <CheckCircle2 className="h-5 w-5 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                              <span className="text-slate-700">{detail}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="bg-slate-50 p-6 rounded-lg border-l-4 border-amber-600">
                          <p className="text-slate-700 italic">
                            <strong className="text-navy-900 not-italic">Why Choose Us: </strong>
                            Our experience in {area.title.toLowerCase()} ensures you receive knowledgeable, 
                            strategic representation tailored to your unique situation.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-slate-600">Get answers to common legal questions</p>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-navy-900 hover:text-amber-600 transition-colors">
                  What is the difference between Chapter 7 and Chapter 13 bankruptcy?
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pt-2">
                  Chapter 7 bankruptcy involves liquidating assets to pay off debts, while Chapter 13 
                  allows you to keep your assets and repay debts over a 3-5 year period through a court-approved 
                  repayment plan. We'll help you determine which option is best for your situation.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-navy-900 hover:text-amber-600 transition-colors">
                  How can you help with IRS tax issues?
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pt-2">
                  We provide comprehensive tax law services including IRS audit representation, negotiating 
                  offers in compromise, setting up payment plans, and resolving tax liens and levies. Our goal 
                  is to minimize your tax liability and resolve disputes efficiently.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-navy-900 hover:text-amber-600 transition-colors">
                  What should I do immediately after a personal injury accident?
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pt-2">
                  First, seek medical attention immediately. Document everything including photos of the scene, 
                  injuries, and gather witness information. Avoid discussing fault with anyone except law enforcement 
                  and your attorney. Contact us as soon as possible so we can protect your rights and begin building your case.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-navy-900 hover:text-amber-600 transition-colors">
                  How much does a consultation cost?
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pt-2">
                  We offer initial consultations to discuss your case and explore your legal options. 
                  Contact us to schedule your consultation and learn more about our fee structure, which varies 
                  depending on the type of case and services required.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="bg-white border-2 border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-navy-900 hover:text-amber-600 transition-colors">
                  How long will my case take?
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pt-2">
                  The timeline varies significantly depending on the type and complexity of your case. Bankruptcy 
                  cases may take 3-6 months, tax disputes can vary widely, and personal injury cases depend on 
                  negotiations and litigation. We'll provide a realistic timeline during your consultation.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Discuss Your Case?</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Don't wait to get the legal help you need. Schedule a consultation today.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PracticeAreas;